import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Mail, Phone, MapPin, ArrowRight } from "lucide-react";
import { SERVICE_OPTIONS, CONTACT } from "@/components/vss/data";

const initial = {
  name: "",
  company: "",
  email: "",
  phone: "",
  service: SERVICE_OPTIONS[0],
  message: "",
  website: "", // honeypot
};

export default function Contact() {
  const [form, setForm] = useState(initial);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = (e) => {
    e.preventDefault();
    // Silent drop if honeypot filled
    if (form.website) return;

    const subject = "Website Enquiry - Visionary Staffing Solutions";
    const body = [
      `Name: ${form.name}`,
      `Company: ${form.company}`,
      `Work Email: ${form.email}`,
      `Phone: ${form.phone}`,
      `Service Needed: ${form.service}`,
      "",
      "Message:",
      form.message,
      "",
      "---",
      "Sent via visionarystaffingsolutions.in contact form",
    ].join("\n");

    const mailto = `mailto:${CONTACT.general}?subject=${encodeURIComponent(
      subject,
    )}&body=${encodeURIComponent(body)}`;
    window.location.href = mailto;

    toast.success("Opening your email app", {
      description: `Your enquiry to ${CONTACT.general} is ready — just hit send.`,
    });
  };

  return (
    <section
      id="contact"
      className="relative py-28 sm:py-36 border-t border-slate-200 bg-slate-50/60"
      data-testid="contact-section"
    >
      <div className="vss-orb bg-teal-200/40 h-[420px] w-[420px] -top-40 left-1/2 -translate-x-1/2" />
      <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
        <div className="grid grid-cols-12 gap-6 mb-14">
          <div className="col-span-12 lg:col-span-7">
            <div className="mb-3 font-mono-a text-[11px] uppercase tracking-[0.28em] text-teal-700">
              / 009 · Build Your Future Workforce
            </div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight leading-[1.05]">
              Let's deploy the team
              <br />
              <span className="font-serif-a italic font-normal text-slate-500">
                that defends your roadmap.
              </span>
            </h2>
          </div>
          <div className="col-span-12 lg:col-span-5">
            <p className="text-slate-600 text-base sm:text-lg leading-relaxed">
              Tell us your stack, timeline, and outcome. Our Pune and Vadodara
              recruitment engines send curated, technically validated profiles
              within <span className="text-teal-700 font-semibold">24–48 hours</span>.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-12 gap-8">
          {/* Form */}
          <motion.form
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-10% 0px" }}
            transition={{ duration: 0.7, ease: "easeOut" }}
            onSubmit={submit}
            className="col-span-12 lg:col-span-7 rounded-3xl border border-slate-200 bg-white p-6 sm:p-10 relative overflow-hidden shadow-[0_18px_44px_rgba(15,23,42,0.06)]"
            data-testid="contact-form"
          >
            {/* Honeypot — hidden from real users, only bots fill it */}
            <div className="vss-honeypot" aria-hidden="true">
              <label>
                Do not fill this field
                <input
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  value={form.website}
                  onChange={update("website")}
                  name="website"
                />
              </label>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
              <Field
                label="Full Name"
                required
                value={form.name}
                onChange={update("name")}
                testId="contact-name-input"
                placeholder="Jane Doe"
              />
              <Field
                label="Company"
                required
                value={form.company}
                onChange={update("company")}
                testId="contact-company-input"
                placeholder="Acme Corp."
              />
              <Field
                label="Work Email"
                type="email"
                required
                value={form.email}
                onChange={update("email")}
                testId="contact-email-input"
                placeholder="you@acme.com"
              />
              <Field
                label="Phone"
                required
                value={form.phone}
                onChange={update("phone")}
                testId="contact-phone-input"
                placeholder="+91 …"
              />
              <div className="sm:col-span-2 flex flex-col gap-2">
                <label className="text-[11px] uppercase tracking-[0.24em] text-slate-500 font-mono-a">
                  Service Needed
                </label>
                <select
                  required
                  value={form.service}
                  onChange={update("service")}
                  data-testid="contact-service-select"
                  className="w-full rounded-xl bg-white border border-slate-300 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none px-4 py-3 text-slate-900"
                >
                  {SERVICE_OPTIONS.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>
              <div className="sm:col-span-2 flex flex-col gap-2">
                <label className="text-[11px] uppercase tracking-[0.24em] text-slate-500 font-mono-a">
                  Message
                </label>
                <textarea
                  required
                  minLength={5}
                  rows={5}
                  value={form.message}
                  onChange={update("message")}
                  data-testid="contact-message-input"
                  placeholder="Roles, stack, timeline, and outcomes you're targeting…"
                  className="w-full rounded-xl bg-white border border-slate-300 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none px-4 py-3 text-slate-900 placeholder:text-slate-400 resize-none"
                />
              </div>
            </div>

            <div className="mt-8 flex items-center justify-between flex-wrap gap-4">
              <p className="text-xs text-slate-500 max-w-[46ch]">
                Clicking below opens your default email app pre-filled with
                your details — no backend, no data stored, straight to
                info@visionarystaffingsolutions.in.
              </p>
              <button
                type="submit"
                data-testid="contact-submit-btn"
                className="group inline-flex items-center gap-3 rounded-full bg-gradient-to-r from-cyan-500 via-teal-500 to-emerald-500 text-white px-6 py-3.5 font-semibold hover:brightness-110 transition shadow-[0_10px_28px_rgba(20,184,166,0.32)]"
              >
                <Mail className="h-4 w-4" />
                Email Visionary
                <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
              </button>
            </div>
          </motion.form>

          {/* Contact info */}
          <div className="col-span-12 lg:col-span-5 flex flex-col gap-5">
            <ContactCard
              tag="General enquiries"
              body={
                <a
                  className="text-teal-700 hover:text-teal-600 link-underline break-all"
                  href={`mailto:${CONTACT.general}`}
                  data-testid="contact-general-email"
                >
                  {CONTACT.general}
                </a>
              }
              icon={<Mail className="h-4 w-4 text-teal-600" />}
            />
            <ContactCard
              tag="Pune office"
              headline={CONTACT.pune.person}
              addressLines={CONTACT.pune.lines}
              phone={CONTACT.pune.phone}
              email={CONTACT.pune.email}
              testIdPrefix="pune"
            />
            <ContactCard
              tag="Vadodara office"
              headline={CONTACT.vadodara.person}
              addressLines={CONTACT.vadodara.lines}
              phone={CONTACT.vadodara.phone}
              email={CONTACT.vadodara.email}
              testIdPrefix="vadodara"
            />
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ label, testId, ...rest }) {
  return (
    <div className="flex flex-col gap-2">
      <label className="text-[11px] uppercase tracking-[0.24em] text-slate-500 font-mono-a">
        {label}
      </label>
      <input
        data-testid={testId}
        className="w-full rounded-xl bg-white border border-slate-300 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none px-4 py-3 text-slate-900 placeholder:text-slate-400"
        {...rest}
      />
    </div>
  );
}

function ContactCard({ tag, headline, body, addressLines, phone, email, icon, testIdPrefix }) {
  return (
    <div
      className="rounded-3xl border border-slate-200 bg-white p-6 sm:p-8 shadow-[0_6px_24px_rgba(15,23,42,0.04)]"
      data-testid={testIdPrefix ? `contact-office-${testIdPrefix}` : "contact-office-general"}
    >
      <div className="flex items-center gap-2 mb-4">
        {icon ?? <MapPin className="h-4 w-4 text-teal-600" />}
        <span className="font-mono-a text-[10px] uppercase tracking-[0.28em] text-teal-700">
          {tag}
        </span>
      </div>
      {headline && (
        <div className="font-display text-lg font-bold text-slate-900">{headline}</div>
      )}
      {addressLines && (
        <div className="mt-2 text-slate-600 text-sm leading-relaxed">
          {addressLines.map((l, i) => (
            <div key={i}>{l}</div>
          ))}
        </div>
      )}
      {phone && (
        <a
          href={`tel:${phone.replace(/\s/g, "")}`}
          className="mt-3 inline-flex items-center gap-2 text-slate-800 hover:text-teal-700 link-underline"
          data-testid={`${testIdPrefix}-phone`}
        >
          <Phone className="h-3.5 w-3.5" /> {phone}
        </a>
      )}
      {email && (
        <div className="mt-2">
          <a
            href={`mailto:${email}`}
            className="inline-flex items-center gap-2 text-teal-700 hover:text-teal-600 link-underline text-sm break-all"
            data-testid={`${testIdPrefix}-email`}
          >
            <Mail className="h-3.5 w-3.5 shrink-0" /> {email}
          </a>
        </div>
      )}
      {body}
    </div>
  );
}
